import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Apiary, Hive, InspectionRecord, BeeBaseAlert, ForageMarker } from '../types';

// Apiary services
export const apiaryService = {
  // Get all apiaries for a user
  async getUserApiaries(userId: string): Promise<Apiary[]> {
    try {
      console.log('=== APIARY DEBUGGING ===');
      console.log('Searching for apiaries for user:', userId);
      
      const q = query(
        collection(db, 'apiaries'),
        where('user_id', '==', userId)
        // Temporarily removed orderBy to avoid index requirement
        // orderBy('created_at', 'desc')
      );
      const snapshot = await getDocs(q);
      const apiaries = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Apiary));
      
      console.log(`Found ${apiaries.length} apiaries for user_id: ${userId}`);
      
      if (apiaries.length === 0) {
        console.log('No apiaries found, checking what exists in database...');
        try {
          // Try to get a few sample apiaries to see structure
          const sampleQuery = query(collection(db, 'apiaries'), limit(5));
          const sampleSnapshot = await getDocs(sampleQuery);
          console.log(`Found ${sampleSnapshot.docs.length} sample apiaries in database`);
          sampleSnapshot.docs.forEach(doc => {
            const data = doc.data();
            console.log('Sample apiary:', doc.id, 'user_id:', data.user_id, 'name:', data.name);
          });
        } catch (e) {
          console.log('Cannot query sample apiaries (security rules)');
        }
      }
      
      console.log('=== END APIARY DEBUGGING ===');
      
      // Sort in JavaScript instead
      return apiaries.sort((a, b) => {
        const aTime = a.created_at && typeof a.created_at === 'object' && 'seconds' in a.created_at 
          ? (a.created_at as any).seconds : 0;
        const bTime = b.created_at && typeof b.created_at === 'object' && 'seconds' in b.created_at 
          ? (b.created_at as any).seconds : 0;
        return bTime - aTime;
      });
    } catch (error) {
      console.error('Error loading user apiaries:', error);
      return [];
    }
  },

  // Add new apiary
  async addApiary(apiary: Omit<Apiary, 'id'>): Promise<string> {
    const docRef = await addDoc(collection(db, 'apiaries'), {
      ...apiary,
      created_at: serverTimestamp()
    });
    return docRef.id;
  },

  // Update apiary
  async updateApiary(apiaryId: string, updates: Partial<Apiary>): Promise<void> {
    const docRef = doc(db, 'apiaries', apiaryId);
    await updateDoc(docRef, updates);
  },

  // Delete apiary
  async deleteApiary(apiaryId: string): Promise<void> {
    const docRef = doc(db, 'apiaries', apiaryId);
    await deleteDoc(docRef);
  },

  // Get single apiary
  async getApiary(apiaryId: string): Promise<Apiary | null> {
    const docRef = doc(db, 'apiaries', apiaryId);
    const snapshot = await getDoc(docRef);
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } as Apiary : null;
  }
};

// Hive services
export const hiveService = {
  // Get all hives for an apiary
  async getApiaryHives(apiaryId: string): Promise<Hive[]> {
    const q = query(
      collection(db, 'hives'),
      where('apiary_id', '==', apiaryId)
      // Temporarily removed orderBy to avoid index requirement
      // orderBy('established', 'desc')
    );
    const snapshot = await getDocs(q);
    const hives = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Hive));
    // Sort in JavaScript instead
    return hives.sort((a, b) => new Date(b.established).getTime() - new Date(a.established).getTime());
  },

  // Get all hives for a user (across all apiaries)
  async getUserHives(userId: string): Promise<Hive[]> {
    try {
      console.log('=== HIVE DEBUGGING ===');
      console.log('Searching for hives for user:', userId);
      
      // First get user's apiaries
      const userApiaries = await apiaryService.getUserApiaries(userId);
      const apiaryIds = userApiaries.map(apiary => apiary.id);
      
      console.log(`User has ${userApiaries.length} apiaries:`, apiaryIds);
      
      if (apiaryIds.length === 0) {
        console.log('No apiaries found, so no hives can be retrieved');
        console.log('=== END HIVE DEBUGGING ===');
        return [];
      }
      
      // Get hives for all apiaries
      const allHives: Hive[] = [];
      for (const apiaryId of apiaryIds) {
        console.log(`Getting hives for apiary: ${apiaryId}`);
        const hives = await this.getApiaryHives(apiaryId);
        console.log(`Found ${hives.length} hives in apiary ${apiaryId}`);
        allHives.push(...hives);
      }
      
      console.log(`Total hives found: ${allHives.length}`);
      console.log('=== END HIVE DEBUGGING ===');
      
      return allHives.sort((a, b) => new Date(b.established).getTime() - new Date(a.established).getTime());
    } catch (error) {
      console.error('Error loading user hives:', error);
      return [];
    }
  },

  // Add new hive
  async addHive(hive: Omit<Hive, 'id'>): Promise<string> {
    const docRef = await addDoc(collection(db, 'hives'), hive);
    return docRef.id;
  },

  // Update hive
  async updateHive(hiveId: string, updates: Partial<Hive>): Promise<void> {
    const docRef = doc(db, 'hives', hiveId);
    await updateDoc(docRef, updates);
  },

  // Delete hive
  async deleteHive(hiveId: string): Promise<void> {
    const docRef = doc(db, 'hives', hiveId);
    await deleteDoc(docRef);
  },

  // Get single hive
  async getHive(hiveId: string): Promise<Hive | null> {
    const docRef = doc(db, 'hives', hiveId);
    const snapshot = await getDoc(docRef);
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } as Hive : null;
  }
};

// Inspection record services
export const inspectionService = {
  // Get all inspection records for a hive
  async getHiveInspections(hiveId: string): Promise<InspectionRecord[]> {
    const q = query(
      collection(db, 'inspections'),
      where('hive_id', '==', hiveId)
      // Temporarily removed orderBy to avoid index requirement
      // orderBy('date', 'desc')
    );
    const snapshot = await getDocs(q);
    const inspections = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as InspectionRecord));
    // Sort in JavaScript instead
    return inspections.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  },

  // Add new inspection record
  async addInspection(inspection: Omit<InspectionRecord, 'id' | 'created_at'>): Promise<string> {
    const docRef = await addDoc(collection(db, 'inspections'), {
      ...inspection,
      created_at: serverTimestamp()
    });
    return docRef.id;
  },

  // Update inspection record
  async updateInspection(inspectionId: string, updates: Partial<InspectionRecord>): Promise<void> {
    const docRef = doc(db, 'inspections', inspectionId);
    await updateDoc(docRef, updates);
  },

  // Delete inspection record
  async deleteInspection(inspectionId: string): Promise<void> {
    const docRef = doc(db, 'inspections', inspectionId);
    await deleteDoc(docRef);
  },

  // Get recent inspections for a user (across all hives)
  async getUserRecentInspections(userId: string, maxResults: number = 10): Promise<InspectionRecord[]> {
    try {
      console.log('=== INSPECTION DEBUGGING ===');
      console.log('Searching for inspections for user:', userId);
      
      let inspections: InspectionRecord[] = [];
      
      // First try: Query inspections by user_id (for merged accounts)
      try {
        const userQuery = query(
          collection(db, 'inspections'),
          where('user_id', '==', userId),
          orderBy('created_at', 'desc'),
          limit(maxResults)
        );
        const userSnapshot = await getDocs(userQuery);
        inspections = userSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as InspectionRecord));
        console.log(`Found ${inspections.length} inspections by user_id`);
        
        if (inspections.length > 0) {
          console.log('=== END INSPECTION DEBUGGING ===');
          return inspections;
        }
      } catch (error) {
        console.log('Error querying by user_id:', error);
      }

      // If no inspections found by user_id, try finding by hive_id (for pre-merge data)
      if (inspections.length === 0) {
        console.log('No inspections found by user_id, trying hive_id approach...');
        
        // Get user's hives first
        const userHives = await hiveService.getUserHives(userId);
        const hiveIds = userHives.map((hive: Hive) => hive.id);
        
        console.log(`User has ${hiveIds.length} hives:`, hiveIds);
        
        if (hiveIds.length > 0) {
          // Query inspections for user's hives (batch approach for multiple hives)
          for (const hiveId of hiveIds) {
            console.log(`Searching inspections for hive: ${hiveId}`);
            try {
              // Try global inspections collection first
              const hiveQuery = query(
                collection(db, 'inspections'),
                where('hive_id', '==', hiveId),
                orderBy('created_at', 'desc')
              );
              const hiveSnapshot = await getDocs(hiveQuery);
              const hiveInspections = hiveSnapshot.docs.map(doc => {
                const data = { id: doc.id, ...doc.data() } as InspectionRecord;
                console.log(`Found inspection in global collection:`, data);
                return data;
              });
              inspections.push(...hiveInspections);
            } catch (error) {
              console.log(`Error querying global inspections for hive ${hiveId}:`, error);
            }
            
            // Also try nested inspections collection under hive
            try {
              console.log(`Trying nested inspections for hive: ${hiveId}`);
              const nestedQuery = query(
                collection(db, 'hives', hiveId, 'inspections'),
                orderBy('created_at', 'desc')
              );
              const nestedSnapshot = await getDocs(nestedQuery);
              const nestedInspections = nestedSnapshot.docs.map(doc => {
                const data = { id: doc.id, ...doc.data() } as InspectionRecord;
                console.log(`Found inspection in nested collection:`, data);
                return data;
              });
              inspections.push(...nestedInspections);
            } catch (error) {
              console.log(`Error querying nested inspections for hive ${hiveId}:`, error);
            }
          }
          console.log(`Total found ${inspections.length} inspections by hive_id`);
        }
      }

      // If still no inspections, check admin status and try broader search
      if (inspections.length === 0) {
        console.log('Still no inspections found, checking what exists in database...');
        try {
          // Try to get a few sample inspections to see structure
          const sampleQuery = query(collection(db, 'inspections'), limit(5));
          const sampleSnapshot = await getDocs(sampleQuery);
          console.log(`Found ${sampleSnapshot.docs.length} sample inspections in database`);
          sampleSnapshot.docs.forEach(doc => {
            const data = doc.data();
            console.log('Sample inspection:', doc.id, 'user_id:', data.user_id, 'hive_id:', data.hive_id);
          });
        } catch (e) {
          console.log('Cannot query sample inspections (security rules)');
        }
      }
      
      console.log('=== END INSPECTION DEBUGGING ===');
      
      // Sort in JavaScript and take limited results
      return inspections
        .sort((a, b) => {
          const aTime = a.created_at && typeof a.created_at === 'object' && 'seconds' in a.created_at 
            ? (a.created_at as any).seconds : 0;
          const bTime = b.created_at && typeof b.created_at === 'object' && 'seconds' in b.created_at 
            ? (b.created_at as any).seconds : 0;
          return bTime - aTime;
        })
        .slice(0, maxResults);
    } catch (error) {
      console.error('Error loading user inspections:', error);
      return [];
    }
  }
};

// BeeBase alert services
export const beebaseService = {
  // Get alerts for a region
  async getRegionAlerts(region: string): Promise<any[]> {
    const q = query(
      collection(db, 'beebase_alerts'),
      where('region', '==', region),
      where('status', '==', 'Active')
    );
    const snapshot = await getDocs(q);
    const alerts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    // Sort by fetchedAt date since that's what we have
    return alerts.sort((a: any, b: any) => new Date(b.fetchedAt).getTime() - new Date(a.fetchedAt).getTime());
  },

  // Get all active alerts
  async getAllActiveAlerts(): Promise<any[]> {
    const q = query(
      collection(db, 'beebase_alerts'),
      where('status', '==', 'Active')
    );
    const snapshot = await getDocs(q);
    const alerts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    // Sort by fetchedAt date since that's what we have
    return alerts.sort((a: any, b: any) => new Date(b.fetchedAt).getTime() - new Date(a.fetchedAt).getTime());
  }
};

// Export utility for BeeBase data
export const exportService = {
  // Generate CSV data for BeeBase export
  generateBeeBaseCSV(inspections: InspectionRecord[], apiaries: Apiary[], hives: Hive[]): string {
    const headers = [
      'Date',
      'Apiary',
      'Hive',
      'Queen Sighted',
      'Temperament',
      'Varroa Check',
      'Weather',
      'Notes'
    ];

    const rows = inspections.map(inspection => {
      const hive = hives.find(h => h.id === inspection.hive_id);
      const apiary = hive ? apiaries.find(a => a.id === hive.apiary_id) : null;
      
      return [
        inspection.date,
        apiary?.name || 'Unknown',
        hive?.name || 'Unknown',
        inspection.queen_sighted ? 'Yes' : 'No',
        inspection.temperament || '',
        inspection.varroa_check ? 'Yes' : 'No',
        inspection.weather || '',
        inspection.notes || ''
      ];
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    return csvContent;
  },

  // Download CSV file
  downloadCSV(csvContent: string, filename: string): void {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

// Forage Marker services
export const forageService = {
  // Get all forage markers for a user's apiaries
  async getUserForageMarkers(userId: string): Promise<ForageMarker[]> {
    // First get user's apiaries
    const userApiaries = await apiaryService.getUserApiaries(userId);
    const apiaryIds = userApiaries.map(apiary => apiary.id);
    
    if (apiaryIds.length === 0) return [];
    
    // Get forage markers for all user apiaries
    const q = query(
      collection(db, 'forage_markers'),
      where('apiaryId', 'in', apiaryIds)
    );
    const snapshot = await getDocs(q);
    const markers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ForageMarker));
    
    // Sort by creation date
    return markers.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  },

  // Get forage markers for a specific apiary
  async getApiaryForageMarkers(apiaryId: string): Promise<ForageMarker[]> {
    const q = query(
      collection(db, 'forage_markers'),
      where('apiaryId', '==', apiaryId)
    );
    const snapshot = await getDocs(q);
    const markers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ForageMarker));
    
    return markers.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  },

  // Add a new forage marker
  async addForageMarker(marker: Omit<ForageMarker, 'id' | 'createdAt'>): Promise<string> {
    const docRef = await addDoc(collection(db, 'forage_markers'), {
      ...marker,
      createdAt: new Date().toISOString()
    });
    return docRef.id;
  },

  // Update an existing forage marker
  async updateForageMarker(markerId: string, updates: Partial<ForageMarker>): Promise<void> {
    const docRef = doc(db, 'forage_markers', markerId);
    await updateDoc(docRef, updates);
  },

  // Delete a forage marker
  async deleteForageMarker(markerId: string): Promise<void> {
    const docRef = doc(db, 'forage_markers', markerId);
    await deleteDoc(docRef);
  },

  // Get single forage marker
  async getForageMarker(markerId: string): Promise<ForageMarker | null> {
    const docRef = doc(db, 'forage_markers', markerId);
    const snapshot = await getDoc(docRef);
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } as ForageMarker : null;
  }
};
